/** @format */
import type { Cell } from '@antv/x6'
import { _$, Drawer, Form, FormItem, Input, Select, Option, Tag, Spinner } from '@king-design/react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { getFlowParamsInfo, NodeTypeEnum, ProcessRecordNodeInputProps } from '../../../container'
import { CountContext } from '../..'
import Readonly from './Readonly'
import { ConcatProps } from 'src/components/AlarmReciver/new'
import NodeInputs from './NodeInputs'
import { getScene } from 'src/api/ApiEventOpsScene'
import { useAsync } from 'react-use'
import TriggerCodeMirror from 'src/pages/EventOps/Trigger/components/RuleDrawer/TriggerCodeMirror'
import { getOutputParams } from 'src/pages/EventOps/Trigger/container'
import { AlarmCallBackNew, AlarmReciverNew } from 'src/components'
import { CallbackValueProp } from 'src/components/AlarmCallBack/new'
import { handleWayToChannels } from 'src/components/AlarmReciver/new'

// 当前节点以前所有节点的输入输出参数
export interface NodePreDecessorsProps {
  id: string
  name: string
  input: string[]
  output: string[]
}
interface NodeDrawerProps {
  className?: string
  visible: boolean
  onClose: () => void
  readonly?: boolean
  nodeData?: Cell.Properties
  onChange: (values) => void
  nodePreDecessors: NodePreDecessorsProps[]
  allEndNodes: string[]
}

export interface NodeFormValuesProps {
  id: string
  name: string
  type?: keyof typeof NodeTypeEnum
  inputs: ProcessRecordNodeInputProps[]
  outputs: string[]
  scriptID: number
  subSceneSign: string
  delay: number
  urlNotices: CallbackValueProp[]
  userNotices: ConcatProps[]
}
const initNodeFormValues: NodeFormValuesProps = {
  id: '',
  name: '',
  inputs: [],
  outputs: [],
  scriptID: null,
  subSceneSign: '',
  delay: 0,
  urlNotices: [],
  userNotices: []
}
// 子场景输入参数渲染
export const renderSubScene = (subSceneInputs: string[], inputs: ProcessRecordNodeInputProps[]) => {
  if (subSceneInputs) {
    return (
      <div className="input_item_description">
        <span>{_$('触发场景所需参数：')}</span>
        {subSceneInputs?.length ? (
          <div className="ksc-space">
            {subSceneInputs?.map(item => {
              const findInput = inputs?.find(input => input.Key === item)
              return (
                <Tag key={item} border="solid" type={findInput ? 'success' : 'danger'}>
                  {item}
                </Tag>
              )
            })}
          </div>
        ) : (
          _$('无')
        )}
      </div>
    )
  } else {
    return '-'
  }
}
const NodeDrawer: React.FC<NodeDrawerProps> = ({
  className,
  visible,
  onClose,
  onChange,
  nodeData,
  allEndNodes,
  nodePreDecessors
}) => {
  const { editable, subSceneList, fetchSubSceneList } = useContext(CountContext)
  const formRef = useRef(null)
  const [formValues, setFormValues] = useState<NodeFormValuesProps>()

  useEffect(() => {
    if (visible && nodeData) {
      console.log('nodeData', nodeData.data)
      setFormValues({
        id: nodeData.id,
        name: nodeData.data.name || '',
        type: nodeData.data.type,
        inputs: nodeData.data.inputs || [],
        outputs: nodeData.data.outputs || [],
        scriptID: nodeData.data.scriptID || null,
        subSceneSign: nodeData.data.subSceneSign || '',
        delay: nodeData.data.delay || 0,
        urlNotices: nodeData.data.urlNotices || [],
        userNotices:
          nodeData.data.userNotices?.map(item => {
            return {
              ...item,
              ContactChannels: handleWayToChannels(item.ContactChannels)
            }
          }) || []
      })
    } else {
      setFormValues(initNodeFormValues)
    }
  }, [visible, nodeData])

  const { value: subSceneInfo } = useAsync(async () => {
    if (visible && formValues?.subSceneSign) {
      const subSceneResult = await getScene(formValues?.subSceneSign)
      const subSceneInputs = getFlowParamsInfo(subSceneResult, 'input')
      const subSceneOutputs = getFlowParamsInfo(subSceneResult, 'output')
      return {
        name: subSceneResult.Name,
        inputs: subSceneInputs,
        outputs: subSceneOutputs
      }
    } else {
      return {
        name: '',
        inputs: [],
        outputs: []
      }
    }
  }, [formValues?.subSceneSign])

  return (
    <Drawer
      value={visible}
      width={800}
      title={_$('节点配置')}
      mode="destroy"
      container={() => document.querySelector('#scene_detail')}
      onChangeValue={show => {
        if (!show) {
          onClose()
        }
      }}
      onClose={() => {
        onClose()
      }}
      ok={async () => {
        const validate = await formRef?.current?.validate()
        if (validate) {
          let outputs = []
          // 子节点
          if (formValues.type === 'AUTOTASK_SUBSCENE') {
            outputs = subSceneInfo.outputs
          } else if (formValues.type === 'GATEWAY' || formValues.type === 'END') {
            // 验证和结束节点
            outputs = formValues.inputs?.map(item => item.Key)
          } else {
            // 其他节点
            outputs = formValues.outputs
          }
          // 验证节点:输入参数赋值到输出参数
          onChange({
            ...formValues,
            outputs
          })
        }
      }}
    >
      {visible ? (
        <>
          {editable ? (
            <Form starOnRequired ref={formRef} labelWidth={90} className={className}>
              <FormItem
                label={_$('节点名称')}
                value={formValues?.name}
                rules={{
                  required: true,
                  unique: (value: string) => {
                    if (formValues.type === 'END') {
                      return allEndNodes.includes(value)
                        ? _$('已经存在与此名称相同的结束节点（本名称无效）')
                        : true
                    }
                    return true
                  }
                }}
              >
                <Input
                  width={662}
                  value={formValues?.name}
                  onChangeValue={value => {
                    setFormValues({ ...formValues, name: value })
                  }}
                />
              </FormItem>
              {/* 子场景 */}
              {formValues?.type === 'AUTOTASK_SUBSCENE' ? (
                <FormItem label={_$('子场景')}>
                  <div className="ksc-space">
                    <Select
                      filterable
                      clearable
                      width={600}
                      value={formValues?.subSceneSign}
                      onChangeValue={value => {
                        setFormValues({ ...formValues, subSceneSign: value })
                      }}
                    >
                      {subSceneList?.map(item => {
                        return (
                          <Option key={item.Sign} value={item.Sign}>
                            {item.Name}
                          </Option>
                        )
                      })}
                    </Select>
                    <a
                      onClick={async () => {
                        await fetchSubSceneList?.()
                        setFormValues({ ...formValues, subSceneSign: '' })
                      }}
                    >
                      {_$('刷新')}
                    </a>
                  </div>
                  <div className="input_item_description">
                    {renderSubScene(subSceneInfo?.inputs, formValues?.inputs)}
                  </div>
                </FormItem>
              ) : null}
              {/* 输入参数 */}
              {formValues?.type !== 'AUTOTASK_TIMER' ? (
                <FormItem label={formValues?.type === 'END' ? _$('流程输出') : _$('输入参数')}>
                  <NodeInputs
                    inputs={formValues?.inputs}
                    onChange={value => {
                      setFormValues({ ...formValues, inputs: value })
                    }}
                    nodePreDecessors={nodePreDecessors}
                  />
                </FormItem>
              ) : null}
              {/* 数据处理 */}
              {formValues?.type === 'AUTOTASK_DISPLAY' ? (
                <FormItem label={_$('数据处理')}>
                  <div className="display_content">
                    <TriggerCodeMirror
                      scriptID={formValues?.scriptID}
                      inputs={formValues?.inputs}
                      onChange={(scriptID, content, lang) => {
                        const outputs = getOutputParams(content, lang)
                        setFormValues(prev => {
                          return { ...prev, scriptID: scriptID, outputs: outputs }
                        })
                      }}
                    />
                  </div>
                </FormItem>
              ) : null}
              {/* 定时器 */}
              {formValues?.type === 'AUTOTASK_TIMER' ? (
                <FormItem label={_$('延迟时长')}>
                  <div className="ksc-space">
                    <Spinner
                      width={50}
                      min={0}
                      forceStep
                      value={formValues.delay}
                      onChangeValue={value => {
                        setFormValues({ ...formValues, delay: value })
                      }}
                    />
                    <span>{_$('秒')}</span>
                  </div>
                </FormItem>
              ) : null}
              {/* 人工介入 */}
              {formValues?.type === 'USERTASK' ? (
                <>
                  <FormItem
                    label={_$('审批联系人')}
                    value={formValues?.userNotices}
                    messages={{
                      required: _$('通知联系人&通知回调至少选择一项配置')
                    }}
                    rules={{
                      required: !formValues?.urlNotices?.length
                    }}
                  >
                    <AlarmReciverNew
                      value={formValues?.userNotices}
                      onChange={value => {
                        setFormValues({ ...formValues, userNotices: value })
                      }}
                    />
                  </FormItem>
                  <FormItem
                    label={_$('审批回调')}
                    value={formValues?.urlNotices}
                    messages={{
                      required: _$('通知联系人&通知回调至少选择一项配置')
                    }}
                    rules={{
                      required: !formValues?.userNotices?.length
                    }}
                  >
                    <div style={{ width: 662 }}>
                      <AlarmCallBackNew
                        showCallbackType={false}
                        value={formValues?.urlNotices}
                        onChange={value => {
                          setFormValues({ ...formValues, urlNotices: value })
                        }}
                      />
                    </div>
                  </FormItem>
                </>
              ) : null}
              {/* 输出参数 */}
              {formValues?.type !== 'AUTOTASK_TIMER' ? (
                <FormItem label={_$('输出参数')}>
                  {formValues?.type === 'AUTOTASK_SUBSCENE' ? (
                    <>
                      <div className="output_items">
                        {subSceneInfo?.outputs?.length
                          ? subSceneInfo?.outputs?.map((item, index) => {
                              return <Tag key={index}>{item}</Tag>
                            })
                          : '-'}
                      </div>
                      <p className="input_item_description">
                        {_$(
                          '子场景的输出参数为其所有结束节点输出的并集，实际运行中可能会有空值的情况'
                        )}
                      </p>
                    </>
                  ) : (
                    <div className="output_items">
                      {formValues?.outputs?.length
                        ? formValues?.outputs?.map((item, index) => {
                            return <Tag key={index}>{item}</Tag>
                          })
                        : '-'}
                    </div>
                  )}
                </FormItem>
              ) : null}
            </Form>
          ) : (
            <Readonly record={formValues} subSceneInfo={subSceneInfo} />
          )}
        </>
      ) : null}
    </Drawer>
  )
}

export default styled(NodeDrawer)`
  .output_items {
    height: 32px;
    line-height: 32px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    .k-tag {
      margin-right: 4px;
    }
  }
  .input_item_description {
    color: #999;
    margin-top: 4px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    .ksc-space {
      margin: 0 4px;
    }
  }
  .display_content {
    width: 662px;
  }
`
